import { useState } from "react";
import { Search, Users, Edit2, Trash2, Plus } from "lucide-react";
import { Button } from "../../ui/button";

export const LeagueTeamsSection = ({ league, onAddTeam, onEditTeam, onDeleteTeam }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [filterDivision, setFilterDivision] = useState("all");

  const filteredTeams = (league.teams || []).filter(team => {
    const matchesSearch = team.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesDivision = filterDivision === "all" || team.division_id === filterDivision;
    return matchesSearch && matchesDivision;
  });

  const getDivisionName = (divId) => {
    return league.divisions?.find(d => d.id === divId)?.name || "Unassigned";
  };

  const getConferenceName = (confId) => {
    return league.conferences?.find(c => c.id === confId)?.name || "";
  };

  return (
    <div className="space-y-4">
      {/* Header & Filters */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
            <input
              type="text"
              placeholder="Search teams..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9 pr-4 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white text-sm w-64"
            />
          </div>
          <select
            value={filterDivision}
            onChange={(e) => setFilterDivision(e.target.value)}
            className="px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white text-sm"
          >
            <option value="all">All Divisions</option>
            {league.divisions?.map(div => (
              <option key={div.id} value={div.id}>{div.name}</option>
            ))}
            <option value="">Unassigned</option>
          </select>
        </div>
        <Button onClick={onAddTeam} className="bg-orange-500 hover:bg-orange-600">
          <Plus className="w-4 h-4 mr-2" /> Add Team
        </Button>
      </div>

      {/* Teams Grid */}
      {filteredTeams.length === 0 ? (
        <div className="text-center py-12 text-zinc-500">
          <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p>No teams found</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredTeams.map(team => (
            <div
              key={team.id}
              className="bg-zinc-800/50 rounded-lg border border-zinc-700 p-4 hover:border-zinc-600 transition-colors"
            >
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  {team.logo_url ? (
                    <img src={team.logo_url} alt="" className="w-12 h-12 object-contain" />
                  ) : (
                    <div
                      className="w-12 h-12 rounded-lg flex items-center justify-center"
                      style={{ backgroundColor: team.color || "#333" }}
                    >
                      <span className="text-white font-bold">{team.abbreviation || team.name.substring(0, 2)}</span>
                    </div>
                  )}
                  <div>
                    <h4 className="text-white font-medium">{team.name}</h4>
                    {team.mascot && <p className="text-zinc-500 text-sm">{team.mascot}</p>}
                    <p className="text-xs text-zinc-600 mt-1">
                      {team.division_id ? getDivisionName(team.division_id) : "No Division"}
                      {team.conference_id && ` • ${getConferenceName(team.conference_id)}`}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => onEditTeam(team)}
                    className="p-1.5 text-zinc-400 hover:text-white hover:bg-zinc-700 rounded"
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => onDeleteTeam(team.id)}
                    className="p-1.5 text-zinc-400 hover:text-red-400 hover:bg-red-500/20 rounded"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
